import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Table, Button, Text } from "@fragments";
import ConfigLoader from "../../components/ConfigLoader";
import ConfigHeaderActions from "../../components/ConfigHeaderActions";
import useConfigHeader from "../../hooks/useConfigHeader";
import Popper from "@fragments/Popper";

import "./styles.css";

import api from "@helpers/api";

import {
  IconCopy,
  IconDotsVertical,
  IconPlus,
  IconSettings,
  IconTag,
  IconTrash,
} from "@tabler/icons-react";

import { useCommunity } from "contexts/community";

import { useParams } from "react-router-dom";

import ManageCouponModal from "@modals/Billing/ManageCouponModal";

export default function Coupons({ info }) {
  const { t } = useTranslation("settings");
  const [loading, setLoading] = useState(true);

  // Page identity lives in the floating settings header; the create action
  // is portaled into it below.
  useConfigHeader({ title: t("coupons.title"), icon: <IconTag /> });

  let { slug } = useParams();

  const [coupons, setCoupons] = useState([]);

  const {
    loading: communityLoading,
    community,
    loadCommunities,
  } = useCommunity();

  const REF_configCouponModal = useRef();

  const popperRef = useRef();

  useEffect(() => {
    if (!communityLoading) handleLoad();
  }, [communityLoading]);

  async function handleLoad() {
    setLoading(true);

    try {
      const currentCommunity = community.getBySlug(slug);
      if (currentCommunity?.id) {
        const res = await api.get(
          `community/${currentCommunity.id}/coupon/list`,
        );
        if (res?.response?.status >= 400) {
          setCoupons([]);
        } else {
          const raw = res?.data;
          const list = Array.isArray(raw) ? raw : (raw?.data ?? []);
          setCoupons([...list]);
        }
      }
    } catch (e) {
      console.log(e);
    }

    setLoading(false);
  }

  function handleConfigureCoupon(id) {
    const currentCommunity = community.getBySlug(slug);
    const plan = id
      ? coupons?.filter((p) => p.id === id)[0]
      : {
          community: currentCommunity.id,
        };

    REF_configCouponModal.current.open(plan, async function (result) {
      console.log(result);
      handleLoad();
    });
  }

  function handleDeleteCoupon(coupon) {
    window.openConfirm({
      title: t("coupons.delete.title"),
      headline: t("shared.warning"),
      subtitle: t("coupons.delete.subtitle"),
      buttons: [
        {
          text: t("shared.deletePermanently"),
          onClick: handleExecuteDelete,
          variant: "contained",
          className: "",
          color: "error",
        },
        {
          text: t("coupons.delete.keep"),
          variant: "text",
        },
      ],
    });

    async function handleExecuteDelete() {
      try {
        const body = {
          duration: {
            type: coupon?.duration?.type ?? "forever",
            months:
              coupon?.duration?.months === undefined ||
              coupon?.duration?.months === null
                ? null
                : coupon.duration.months,
          },
          code: coupon?.code,
          percent_off: coupon?.percent_off,
        };
        const res = await api.delete(`community/coupon/${coupon.id}`, body);
        if (res?.response?.status >= 400) {
          window.setSnackbar(
            res?.response?.data?.message || t("coupons.delete.error"),
          );
          return;
        }
        if (res?.status === 204) {
          window.setSnackbar(t("coupons.delete.success"));
          await handleLoad();
        }
      } catch (e) {
        console.log(e);
      }
    }
  }

  function handleOpenPlanOptions(event, planId) {
    const options = [
      {
        id: "configure",
        label: t("shared.configure"),
        icon: <IconSettings size={16} />,
        onClick: async () => {
          handleConfigureCoupon(planId);
          popperRef.current.close();
        },
      },
      {
        id: "delete",
        label: t("coupons.delete.menu"),
        icon: <IconTrash size={16} />,
        variant: "danger",
        onClick: async () => {
          const c = coupons.find((x) => x.id === planId);
          if (c) handleDeleteCoupon(c);
          popperRef.current.close();
        },
      },
    ];

    popperRef.current.open(options, "bottom", event.currentTarget);
  }

  function handleCopy(code) {
    const link = `https://pay.ginastee.com/${community.getBySlug(slug).checkout}?coupon=${code}`;

    console.log(link);
    navigator.clipboard
      .writeText(link)
      .then(() => {
        window.setSnackbar(t("shared.linkCopied"));
      })
      .catch((err) => {
        console.error("Erro ao copiar:", err);
        window.setSnackbar(t("shared.copyLinkError"));
      });
  }

  const columns = [
    {
      field: "code",
      title: t("coupons.columns.code"),
      grow: 1,
      render: (item) => item.code,
    },
    {
      title: t("coupons.columns.duration"),
      width: "140px",
      align: "center",
      customRender: (item) => {
        const map = {
          forever: t("coupons.duration.forever"),
          once: t("coupons.duration.once"),
          repeating: t("coupons.duration.repeating", { months: item.duration?.months }),
        };
        return (
          <Text.span contrast={6}>
            {map[item.duration?.type] || item.duration?.type || "—"}
          </Text.span>
        );
      },
    },
    {
      title: t("coupons.columns.discount"),
      width: "120px",
      align: "center",
      customRender: (item) => (
        <Text.span contrast={6} weight={500}>
          {item.percent_off}%
        </Text.span>
      ),
    },
  ];

  function CreateCouponButton() {
    return (
      <Button
        onClick={() => handleConfigureCoupon()}
        endIcon={<IconPlus />}
        text={t("coupons.createCoupon")}
        size="small"
      />
    );
  }

  return (
    <div
      className="config-page config-general-community config-plans-page"
      key={loading}
    >
      <ConfigHeaderActions>
        <CreateCouponButton />
      </ConfigHeaderActions>

      {
        <div className="mt-4" key={Math.random()}>
          <div className="px-3">
            <Table
              id="community-coupons-table"
              columns={columns}
              data={coupons}
              loading={loading}
              contained={true}
              actionsComponent={(item) => (
                <>
                  <Button
                    color="overlay"
                    variant="text"
                    size="medium"
                    icon={<IconCopy size={16} />}
                    onClick={() => handleCopy(item.code)}
                    tooltip={t("shared.checkoutLink")}
                  />
                  <Button
                    color="overlay"
                    variant="text"
                    size="medium"
                    icon={<IconSettings size={16} />}
                    onClick={() => handleConfigureCoupon(item.id)}
                    tooltip={t("shared.configure")}
                  />
                  <Button
                    color="overlay"
                    variant="text"
                    size="medium"
                    icon={<IconDotsVertical size={16} />}
                    onMouseEnter={(event) =>
                      handleOpenPlanOptions(event, item.id)
                    }
                    onClick={(event) => handleOpenPlanOptions(event, item.id)}
                    tooltip={t("shared.moreOptions")}
                  />
                </>
              )}
            />
          </div>
        </div>
      }

      <Popper controlRef={popperRef} config={{ activateOnHover: true }} />

      <ManageCouponModal modalRef={REF_configCouponModal} />
    </div>
  );
}
